import Axios from "axios";
import { performSignOut } from "../utils/performSignOut";

const apiBaseUrl = (process.env.REACT_APP_API_URL ?? "http://localhost:3001").replace(/\/+$/, "");
const apiVersion = process.env.REACT_APP_API_VERSION ?? "v1";
const API_BASE_URL = `${apiBaseUrl}/${apiVersion}`;

const axiosRefresh = Axios.create({
    baseURL: API_BASE_URL,
    timeout: 90000,
});

let refreshPromise: Promise<string> | null = null;

const isValidTokenFormat = (token?: string | null): boolean => {
    return Boolean(token && token.split(".").length === 3);
};

export const refreshTokenOnce = async (): Promise<string> => {
    if (refreshPromise) {
        console.log("[auth] waiting existing refresh...");
        return refreshPromise;
    }

    refreshPromise = (async () => {
        const refreshToken = sessionStorage.getItem("refresh_token");

        if (!refreshToken || !isValidTokenFormat(refreshToken)) {
            throw new Error("Missing or invalid refresh token");
        }

        console.log("[auth] refreshing token...");

        const response = await axiosRefresh.post(
            "/tokens/refresh",
            {},
            {
                headers: {
                    Authorization: `Bearer ${refreshToken}`,
                },
            },
        );

        const newToken = response.data?.data?.token;
        const refreshTokenResponse = response.data?.data?.refresh_token;

        const newRefreshToken =
            typeof refreshTokenResponse === "string"
                ? refreshTokenResponse
                : refreshTokenResponse?.token;

        if (!newToken || !isValidTokenFormat(newToken)) {
            throw new Error("Invalid access token received from refresh");
        }

        if (!newRefreshToken || !isValidTokenFormat(newRefreshToken)) {
            throw new Error("Invalid refresh token received from refresh");
        }

        sessionStorage.setItem("token", newToken);
        sessionStorage.setItem("refresh_token", newRefreshToken);

        console.log("[auth] refresh success");

        return newToken;
    })();

    try {
        return await refreshPromise;
    } finally {
        refreshPromise = null;
    }
};

export const clearSessionAndRedirect = () => {
    void (async () => {
        await performSignOut();

        if (!window.location.pathname.includes("/signin")) {
            window.location.href = "/signin";
        }
    })();
};

export const isTokenExpiredResponse = (error: any): boolean => {
    const message = String(error?.response?.data?.message || "").toLowerCase();
    const code = error?.response?.data?.code || "";

    return (
        code === "TOKEN_EXPIRED" ||
        message.includes("expired") ||
        message.includes("expirada") ||
        message.includes("expirado") ||
        message.includes("jwt expired") ||
        message.includes("token expired")
    );
};

export const isInvalidSignatureResponse = (error: any): boolean => {
    const message = String(error?.response?.data?.message || "").toLowerCase();
    const code = error?.response?.data?.code || "";

    return (
        code === "TOKEN_SIGNATURE_INVALID" ||
        message.includes("invalid refresh token signature") ||
        message.includes("token signature is invalid") ||
        message.includes("invalid token signature")
    );
};